// The Performance page's data pipeline, extracted unchanged so the dashboard preview plots exactly what the page
// plots. Nothing here may be re-implemented elsewhere: ATL/CTL/TSB/ACWR, rest-day decay, same-day grouping,
// the load baseline and the date handling are the page's existing behaviour.
import { getLoadBaseline, isValidDateRange } from './readinessUtils';
import { startOfLocalDay } from '../../../Utils/dates/localDay';

export const combineWorkoutsForSameDay = workouts => {
    const workoutsByDate = workouts.reduce((acc, workout) => {
        const date = new Date(workout.workoutStart).toLocaleDateString('el-GR');

        if (!acc[date]) {
            acc[date] = {
                workoutStart: new Date(workout.workoutStart).setHours(0, 0, 0, 0),
                workoutEnd: new Date(workout.workoutStart).setHours(23, 59, 59, 999),
                trainingDurationMin: 0,
                trainingDurationSec: 0,
                volume: 0,
                css: workout.css,
                ftp: workout.ftp,
                nss: workout.nss,
                if: 0,
                tl: 0,
                tss: 0,
                atl: workout.atl,
                ctl: workout.ctl,
                tsb: workout.tsb,
                acwr: workout.acwr,
                workoutCount: 0
            };
        }

        const current = acc[date];
        current.trainingDurationMin += workout.trainingDurationMin || 0;
        current.trainingDurationSec += workout.trainingDurationSec || 0;
        current.volume += workout.volume || 0;
        current.tl += workout.tl || 0;
        current.tss += workout.tss || 0;
        current.if =
            (current.if * current.workoutCount + (workout.if || 0)) / (current.workoutCount + 1);
        current.workoutCount += 1;

        current.atl = workout.atl;
        current.ctl = workout.ctl;
        current.tsb = workout.tsb;
        current.acwr = workout.acwr;
        current.css = workout.css;
        current.ftp = workout.ftp;
        current.nss = workout.nss;

        return acc;
    }, {});

    // Convert the grouped workouts back to an array
    return Object.entries(workoutsByDate).map(([date, workout]) => ({
        ...workout,
        trainingDurationMin:
            workout.trainingDurationMin + Math.floor(workout.trainingDurationSec / 60),
        trainingDurationSec: workout.trainingDurationSec % 60
    }));
};

export const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const currentDate = new Date(startDate);
    const lastDate = new Date(endDate);

    while (currentDate <= lastDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
};

export function calculateTrainingLoadFrontend(prev, tss, daysSinceLast) {
    let atl = 0;
    let ctl = 0;

    if (prev == null) {
        atl = tss / 7;
        ctl = tss / 28;
    } else if (daysSinceLast === 0 || daysSinceLast === 1) {
        atl = prev.atl + (tss - prev.atl) * (1 / 7);
        ctl = prev.ctl + (tss - prev.ctl) * (1 / 28);
    } else {
        let yesterdayATL = prev.atl;
        let yesterdayCTL = prev.ctl;
        for (let i = 1; i < daysSinceLast; i++) {
            yesterdayATL = yesterdayATL + (0 - yesterdayATL) * (1 / 7);
            yesterdayCTL = yesterdayCTL + (0 - yesterdayCTL) * (1 / 28);
        }
        atl = yesterdayATL + (tss - yesterdayATL) * (1 / 7);
        ctl = yesterdayCTL + (tss - yesterdayCTL) * (1 / 28);
    }
    const tsb = ctl - atl;
    const acwr = ctl ? atl / ctl : 0;
    return { atl, ctl, tsb, acwr };
}

/** Completed and Assigned workouts, oldest first (the page's fetch transform). */
export const prepareWorkouts = performances =>
    performances
        .filter(workout => workout.status === 'Completed' || workout.status === 'Assigned')
        .sort((a, b) => new Date(b.workoutStart) - new Date(a.workoutStart))
        .reverse();

/** The page's range shortcuts: `today` minus one calendar period via the native Date API (local), as ISO dates. */
export const shortcutRange = (period, today = new Date()) => {
    const endDate = today.toISOString().split('T')[0];
    const start = new Date(today);
    switch (period) {
        case 'year':
            start.setFullYear(start.getFullYear() - 1);
            break;
        case '6months':
            start.setMonth(start.getMonth() - 6);
            break;
        case 'month':
            start.setMonth(start.getMonth() - 1);
            break;
        case 'week':
            start.setDate(start.getDate() - 7);
            break;
        default:
            break;
    }
    const startDate = start.toISOString().split('T')[0];
    return { startDate, endDate };
};

export const monthShortcutRange = today => shortcutRange('month', today);

/** One entry per local day in the range, from the page's former useMemo. */
export const buildPerformanceSeries = (data, dateRange) => {
    let workouts = data;
    let startDate, endDate;

    // Only filter if both dates are provided and valid
    if (isValidDateRange(dateRange.startDate, dateRange.endDate)) {
        // Date-only inputs are local calendar days; the end day is included through its last millisecond.
        startDate = startOfLocalDay(dateRange.startDate);
        endDate = startOfLocalDay(dateRange.endDate);
        endDate.setHours(23, 59, 59, 999);

        workouts = data.filter(workout => {
            const workoutDate = new Date(workout.workoutStart);
            return workoutDate >= startDate && workoutDate <= endDate;
        });
    } else {
        startDate = new Date(Math.min(...data.map(w => new Date(w.workoutStart))));
        endDate = new Date(Math.max(...data.map(w => new Date(w.workoutStart))));
    }

    const allDates = getDatesInRange(startDate, endDate);
    // Separate completed and assigned workouts
    const completedWorkouts = workouts.filter(w => w.status === 'Completed');
    const assignedWorkouts = workouts.filter(w => w.status === 'Assigned');
    const combinedWorkouts = combineWorkoutsForSameDay(completedWorkouts);
    const workoutsByDate = combinedWorkouts.reduce((acc, workout) => {
        const dateStr = new Date(workout.workoutStart).toLocaleDateString('el-GR');
        acc[dateStr] = workout;
        return acc;
    }, {});

    // Add assigned workouts to the map (for planning purposes)
    assignedWorkouts.forEach(workout => {
        const dateStr = new Date(workout.workoutStart).toLocaleDateString('el-GR');
        if (!workoutsByDate[dateStr]) {
            workoutsByDate[dateStr] = {
                workoutStart: new Date(workout.workoutStart).setHours(0, 0, 0, 0),
                workoutEnd: new Date(workout.workoutStart).setHours(23, 59, 59, 999),
                trainingDurationMin: workout.trainingDurationMin || 0,
                trainingDurationSec: workout.trainingDurationSec || 0,
                volume: workout.volume || 0,
                css: workout.css || 1.0,
                ftp: workout.ftp || 0,
                nss: workout.nss || 0,
                if: 0,
                tl: 0,
                tss: 0,
                atl: null,
                ctl: null,
                tsb: null,
                acwr: null,
                workoutCount: 0,
                status: 'Assigned',
                isAssigned: true
            };
        } else {
            workoutsByDate[dateStr].isAssigned = true;
            workoutsByDate[dateStr].assignedVolume =
                (workoutsByDate[dateStr].assignedVolume || 0) + (workout.volume || 0);
        }
    });

    // A range opening on rest days continues from the preceding completed workout
    // rather than from zero load.
    let { prev, lastWorkoutDate } = getLoadBaseline(data, startDate);
    const mapped = allDates.map(date => {
        const dateStr = date.toLocaleDateString('el-GR');
        let entry;
        if (workoutsByDate[dateStr]) {
            entry = { ...workoutsByDate[dateStr] };
            // Use server-calculated ATL/CTL/TSB for workout days — they account for full
            // training history, not just the selected date range.
            if (entry.tss > 0 || entry.workoutCount > 0) {
                prev = { atl: entry.atl, ctl: entry.ctl };
                lastWorkoutDate = date;
            }
        } else {
            const tss = 0;
            const daysSinceLast =
                lastWorkoutDate == null
                    ? null
                    : Math.round((date - lastWorkoutDate) / (1000 * 60 * 60 * 24));
            const { atl, ctl, tsb, acwr } = calculateTrainingLoadFrontend(
                prev,
                tss,
                daysSinceLast == null ? 0 : daysSinceLast
            );
            entry = {
                workoutStart: date.setHours(0, 0, 0, 0),
                workoutEnd: date.setHours(23, 59, 59, 999),
                trainingDurationMin: 0,
                trainingDurationSec: 0,
                volume: 0,
                css: combinedWorkouts[combinedWorkouts.length - 1]?.css || 1.0,
                ftp: combinedWorkouts[combinedWorkouts.length - 1]?.ftp || 0,
                nss: combinedWorkouts[combinedWorkouts.length - 1]?.nss || 0,
                if: 0,
                tl: 0,
                tss: 0,
                atl,
                ctl,
                tsb,
                acwr,
                workoutCount: 0
            };
        }
        return entry;
    });
    return { filteredData: mapped };
};

export const buildChartDataSource = (filteredData, showEmptyDays) =>
    showEmptyDays ? filteredData : filteredData.filter(w => w.workoutCount > 0);
